import type { NavigationGuardWithThis, RouteLocationRaw } from 'vue-router';
import type { Permission } from '../api/permissions.api.types.ts';
import { usePermissionsStore } from '../store/permissions.store.ts';
import type { PermissionCheckMode } from '../types.ts';

export type PermissionGuardOptions = {
  /** Route to redirect to when the check fails. */
  redirect: RouteLocationRaw;
  /**
   * How to combine multiple keys.
   * - `'any'` (default): at least one key must be granted.
   * - `'all'`: every key must be granted.
   */
  mode?: PermissionCheckMode;
};

/**
 * Vue Router `beforeEnter` factory that gates a route on one or more
 * permissions. Mirrors the shape of `featureFlagGuard` from
 * `@tundr/feature-flags`.
 *
 * The check goes through `usePermissionsStore().hasPermission` — the
 * single feature-flag–gated entry point — so with the RBAC flag OFF
 * the guard always allows (ship-before-BE).
 *
 * No awaiting of bootstrap state: the HR `/app` parent route's
 * `sessionInitGuard` already awaits the `SESSION_INIT` lifecycle phase
 * (which includes the awaited permissions fetch), so by the time this
 * guard runs the store is hydrated.
 */
export function permissionGuard(
  required: Permission | Permission[],
  { redirect, mode = 'any' }: PermissionGuardOptions,
): NavigationGuardWithThis<undefined> {
  return () => {
    const keys = Array.isArray(required) ? required : [required];
    if (keys.length === 0) return redirect;

    const { hasPermission } = usePermissionsStore();
    const allowed =
      mode === 'all'
        ? keys.every((key) => hasPermission(key))
        : keys.some((key) => hasPermission(key));

    return allowed ? true : redirect;
  };
}
